import opportunityConfigurationService from "@/services/api/opportunityConfiguration";
import { handleResponse } from "@/services/axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface LoadingState {
    opportunityConfigurationData: boolean;
    opportunityCustomFieldData: boolean;
    opportunityCustomFields: boolean;
};

interface ConfigState {
    opportunityConfigurationData: any[];
    opportunityCustomFieldData: any[];
    opportunityCustomFields: any[];
    loading: LoadingState;
    error: string | null;
};

const initialState: ConfigState = {
    opportunityConfigurationData: [],
    opportunityCustomFieldData: [],
    opportunityCustomFields: [],
    loading: {
        opportunityConfigurationData: false,
        opportunityCustomFieldData: false,
        opportunityCustomFields: false,
    },
    error: null,
};

export const getOpportunityConfigurationCustomField = createAsyncThunk(
    "opportunityConfiguration/getOpportunityConfigurationCustomField",
    async (_, { rejectWithValue }) => {
        try {
            const response = await opportunityConfigurationService.getOpportunityConfigurationCustomField();
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message ?? "Failed to add opportunity configuration field");
        }
    },
);

export const fetchOpportunityConfigurationFields = createAsyncThunk(
    "opportunityConfiguration/fetchOpportunityConfigurationFields",
    async (_, { rejectWithValue }) => {
        try {
            const response = await opportunityConfigurationService.fetchOpportunityConfigurationFields();
            return handleResponse(response, rejectWithValue);
        } catch (error: any) {
            return rejectWithValue(error?.Message ?? "Failed to fetch opportunity configuration fields");
        }
    },
);

export const updateOpportunityConfigurationCustomFields = createAsyncThunk(
    "opportunityConfiguration/updateOpportunityConfigurationCustomFields",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await opportunityConfigurationService.updateOpportunityConfigurationCustomFields(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message ?? "Failed to update opportunity configuration fields");
        }
    },
);

export const deleteOpportunityConfigurationCustomFields = createAsyncThunk(
    "opportunityConfiguration/deleteOpportunityConfigurationCustomFields",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await opportunityConfigurationService.deleteOpportunityConfigurationCustomFields(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message || "Failed to delete opportunity configuration field");
        }
    }
);

export const getOpportunityCustomFields = createAsyncThunk(
    "opportunityConfiguration/getOpportunityCustomFields",
    async (_, { rejectWithValue }) => {
        try {
            const response = await opportunityConfigurationService.getOpportunityCustomFields();
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message || "Failed to fetch opportunity custom fields");
        }
    }
);

export const deleteOpportunityCustomField = createAsyncThunk(
    "opportunityConfiguration/deleteOpportunityCustomField",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await opportunityConfigurationService.deleteOpportunityCustomField(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message || "Failed to delete opportunity custom field");
        }
    }
);

export const addOpportunityCustomField = createAsyncThunk(
    "opportunityConfiguration/addOpportunityCustomField",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await opportunityConfigurationService.addOpportunityCustomField(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message || "Failed to add opportunity custom field");
        }
    }
);

export const updateOpportunityCustomField = createAsyncThunk(
    "opportunityConfiguration/updateOpportunityCustomField",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await opportunityConfigurationService.updateOpportunityCustomField(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message || "Failed to update opportunity custom field");
        }
    }
);

const opportunityConfigurationSlice = createSlice({
    name: "opportunityConfiguration",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(fetchOpportunityConfigurationFields.pending, (state) => {
                state.loading.opportunityConfigurationData = true;
                state.error = null;
                state.opportunityConfigurationData = [];
            })
            .addCase(fetchOpportunityConfigurationFields.fulfilled, (state, action) => {
                state.loading.opportunityConfigurationData = false;
                state.opportunityConfigurationData = action.payload || [];
            })
            .addCase(fetchOpportunityConfigurationFields.rejected, (state, action) => {
                state.loading.opportunityConfigurationData = false;
                state.error = action.payload as string;
            })

            .addCase(getOpportunityConfigurationCustomField.pending, (state) => {
                state.loading.opportunityCustomFieldData = true;
                state.error = null;
                state.opportunityCustomFieldData = [];
            })
            .addCase(getOpportunityConfigurationCustomField.fulfilled, (state, action) => {
                state.loading.opportunityCustomFieldData = false;
                state.opportunityCustomFieldData = action.payload || [];
            })
            .addCase(getOpportunityConfigurationCustomField.rejected, (state, action) => {
                state.loading.opportunityCustomFieldData = false;
                state.error = action.payload as string;
            })

            .addCase(getOpportunityCustomFields.pending, (state) => {
                state.loading.opportunityCustomFields = true;
                state.opportunityCustomFields = [];
            })
            .addCase(getOpportunityCustomFields.fulfilled, (state, action) => {
                state.loading.opportunityCustomFields = false;
                state.opportunityCustomFields = action.payload || [];
            })
            .addCase(getOpportunityCustomFields.rejected, (state, action) => {
                state.loading.opportunityCustomFields = false;
                state.error = action.payload as string;
            });
    },
});

export default opportunityConfigurationSlice.reducer;
